/**
 * Structured Data (JSON-LD) for Rich Snippets
 * Implements Schema.org markup for better search visibility
 */

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://lavvissa.com';

/**
 * Adds or updates a JSON-LD script in the document head
 * @param {Object} data - Structured data object
 * @param {string} id - Unique identifier for this script
 */
export const setStructuredData = (data, id = 'structured-data') => {
  if (!data) return;

  // Remove existing script with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Create new script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * Removes structured data by ID
 * @param {string} id - Script ID to remove
 */
export const removeStructuredData = (id = 'structured-data') => {
  const script = document.getElementById(id);
  if (script) script.remove();
};

/**
 * Organization schema for the website
 * @returns {Object} - Organization JSON-LD
 */
export const getOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Lavvissa',
    alternateName: 'لاویسا',
    url: SITE_URL,
    logo: `${SITE_URL}/lavvissa-logo.png`,
    description: 'فروشگاه آنلاین مد و پوشاک با بهترین برندهای ترکیه و جهان',
    sameAs: [
      'https://www.instagram.com/lavvissa',
      'https://www.facebook.com/lavvissa',
      'https://twitter.com/lavvissa',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      availableLanguage: ['Persian', 'English'],
    },
  };
};

/**
 * Website schema with search action
 * @returns {Object} - WebSite JSON-LD with SearchAction
 */
export const getWebsiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Lavvissa',
    alternateName: 'لاویسا',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

/**
 * BreadcrumbList schema for navigation
 * @param {Array<Object>} breadcrumbs - Array of breadcrumb items [{name, url}]
 * @returns {Object} - BreadcrumbList JSON-LD
 */
export const getBreadcrumbSchema = (breadcrumbs = []) => {
  if (breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url ? `${SITE_URL}${crumb.url}` : undefined,
    })),
  };
};

/**
 * Product schema for product pages
 * @param {Object} product - Product data
 * @param {string} language - Current language
 * @returns {Object} - Product JSON-LD
 */
export const getProductSchema = (product, language = 'fa') => {
  if (!product) return null;

  const name = product.name?.[language] || product.name?.fa;
  const description = product.description?.[language] || product.description?.fa;
  const image = product.media?.featuredImage;
  const images = product.media?.gallery || [];
  const price = product.pricing?.basePrice?.toman || product.pricing?.basePrice?.TL || 0;
  const currency = product.pricing?.basePrice?.toman ? 'IRR' : 'TRY';
  const availability = product.stock?.isInStock || product.sourceInfo?.isAvailable
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';
  const brand = product.brand?.name;
  const sku = product.sku;
  const rating = product.metrics?.avgRating;
  const reviewCount = product.metrics?.reviewCount || 0;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: image ? [image, ...images] : images,
    sku,
    brand: brand ? {
      '@type': 'Brand',
      name: brand,
    } : undefined,
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.slug}`,
      priceCurrency: currency,
      price: price.toString(),
      availability,
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      seller: {
        '@type': 'Organization',
        name: 'Lavvissa',
      },
    },
  };

  // Add aggregate rating if available
  if (rating && reviewCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.toString(),
      reviewCount: reviewCount.toString(),
      bestRating: '5',
      worstRating: '1',
    };
  }

  return schema;
};

/**
 * ItemList schema for product collections/categories
 * @param {Array<Object>} products - Array of products
 * @param {Object} meta - Collection metadata
 * @returns {Object} - ItemList JSON-LD
 */
export const getProductListSchema = (products = [], meta = {}) => {
  if (products.length === 0) return null;

  const { name, url } = meta;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: name || 'Products',
    url: url ? `${SITE_URL}${url}` : undefined,
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/product/${product.slug}`,
    })),
  };
};

/**
 * Review schema for product reviews
 * @param {Object} review - Review data
 * @param {Object} product - Product data
 * @returns {Object} - Review JSON-LD
 */
export const getReviewSchema = (review, product) => {
  if (!review || !product) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Product',
      name: product.name?.fa || product.name,
      image: product.media?.featuredImage,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating?.toString(),
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: review.userName || 'Anonymous',
    },
    reviewBody: review.comment,
    datePublished: review.createdAt,
  };
};

/**
 * FAQ schema for FAQ pages
 * @param {Array<Object>} faqs - Array of FAQ items [{question, answer}]
 * @returns {Object} - FAQPage JSON-LD
 */
export const getFAQSchema = (faqs = []) => {
  if (faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * LocalBusiness schema (if Lavvissa has physical location)
 * @param {Object} location - Location data
 * @returns {Object} - LocalBusiness JSON-LD
 */
export const getLocalBusinessSchema = (location = {}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ClothingStore',
    '@id': SITE_URL,
    name: 'Lavvissa',
    image: `${SITE_URL}/lavvissa-logo.png`,
    url: SITE_URL,
    telephone: location.phone,
    address: location.address ? {
      '@type': 'PostalAddress',
      streetAddress: location.address.street,
      addressLocality: location.address.city,
      postalCode: location.address.postalCode,
      addressCountry: location.address.country || 'IR',
    } : undefined,
    geo: location.geo ? {
      '@type': 'GeoCoordinates',
      latitude: location.geo.lat,
      longitude: location.geo.lng,
    } : undefined,
    priceRange: '$$',
    openingHoursSpecification: location.hours || [],
  };
};

/**
 * Offer schema for special promotions
 * @param {Object} offer - Offer/coupon data
 * @returns {Object} - Offer JSON-LD
 */
export const getOfferSchema = (offer) => {
  if (!offer) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: offer.name,
    description: offer.description,
    url: `${SITE_URL}/shop?coupon=${offer.code}`,
    priceSpecification: {
      '@type': 'PriceSpecification',
      price: offer.discount || '0',
      priceCurrency: 'IRR',
    },
    validFrom: offer.startDate,
    validThrough: offer.endDate,
    seller: {
      '@type': 'Organization',
      name: 'Lavvissa',
    },
  };
};

/**
 * Sets up base structured data for the entire site
 */
export const setupBaseStructuredData = () => {
  // Organization
  setStructuredData(getOrganizationSchema(), 'organization-schema');

  // Website with search
  setStructuredData(getWebsiteSchema(), 'website-schema');
};

/**
 * Sets structured data for product page
 * @param {Object} product - Product data
 * @param {Array<Object>} breadcrumbs - Breadcrumb trail
 * @param {string} language - Current language
 */
export const setProductStructuredData = (product, breadcrumbs = [], language = 'fa') => {
  // Product schema
  const productSchema = getProductSchema(product, language);
  if (productSchema) {
    setStructuredData(productSchema, 'product-schema');
  }

  // Breadcrumb schema
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbs);
  if (breadcrumbSchema) {
    setStructuredData(breadcrumbSchema, 'breadcrumb-schema');
  }
};

/**
 * Sets structured data for product listing/category page
 * @param {Array<Object>} products - Products to display
 * @param {Object} meta - Page metadata
 * @param {Array<Object>} breadcrumbs - Breadcrumb trail
 */
export const setProductListStructuredData = (products, meta = {}, breadcrumbs = []) => {
  // Product list schema
  const listSchema = getProductListSchema(products, meta);
  if (listSchema) {
    setStructuredData(listSchema, 'product-list-schema');
  }

  // Breadcrumb schema
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbs);
  if (breadcrumbSchema) {
    setStructuredData(breadcrumbSchema, 'breadcrumb-schema');
  }
};

/**
 * Clears all structured data scripts
 */
export const clearAllStructuredData = () => {
  document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
    // Keep base schemas (organization, website)
    if (script.id !== 'organization-schema' && script.id !== 'website-schema') {
      script.remove();
    }
  });
};

const StructuredData = {
  setStructuredData,
  removeStructuredData,
  getOrganizationSchema,
  getWebsiteSchema,
  getBreadcrumbSchema,
  getProductSchema,
  getProductListSchema,
  getReviewSchema,
  getFAQSchema,
  getLocalBusinessSchema,
  getOfferSchema,
  setupBaseStructuredData,
  setProductStructuredData,
  setProductListStructuredData,
  clearAllStructuredData,
};

export default StructuredData;
